import { createElement, Fragment, useSyncExternalStore, type ReactNode } from 'react';

/**
 * Idiomas de la interfaz. El texto fuente está en español y sirve de clave: `t('Cerrar sesión')` devuelve la
 * traducción del idioma activo o, si falta, el mismo texto en español. Los catálogos (`locales/<idioma>/*.ts`)
 * se cargan solo para el idioma elegido.
 *
 * El idioma sale de la preferencia guardada en este navegador; sin preferencia ("auto"), del primer idioma del
 * navegador que la aplicación conozca, y si ninguno coincide, del español.
 *
 * `t()` se llama al pintar, nunca al cargar un módulo: el catálogo todavía no está listo en ese momento.
 */
export type Locale = 'es' | 'en' | 'fr' | 'de';
export type LocalePref = Locale | 'auto';
export type Messages = Record<string, string>;
export type Vars = Record<string, string | number>;

/** Nombre de cada idioma en su propia lengua, como se muestra en el selector. */
export const LOCALES: ReadonlyArray<{ code: Locale; name: string }> = [
  { code: 'es', name: 'Español' },
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'Français' },
  { code: 'de', name: 'Deutsch' },
];
export const DEFAULT_LOCALE: Locale = 'es';
const STORAGE_KEY = 'lang';

const isLocale = (v: unknown): v is Locale => LOCALES.some((l) => l.code === v);

/** Primer idioma de la lista del navegador que la aplicación conoce ("fr-CA" cuenta como francés). */
export function browserLocale(langs: readonly string[] = typeof navigator === 'undefined' ? [] : navigator.languages?.length ? navigator.languages : [navigator.language]): Locale {
  for (const l of langs) {
    const base = l?.toLowerCase().split('-')[0];
    if (isLocale(base)) return base;
  }
  return DEFAULT_LOCALE;
}

export function readPref(): LocalePref {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return isLocale(v) ? v : 'auto';
  } catch {
    return 'auto';
  }
}

const resolve = (pref: LocalePref): Locale => (pref === 'auto' ? browserLocale() : pref);

const loaders = import.meta.glob<{ default: Messages }>('./locales/*/*.ts');

async function loadMessages(locale: Locale): Promise<Messages> {
  if (locale === DEFAULT_LOCALE) return {};
  const prefix = `./locales/${locale}/`;
  const parts = await Promise.all(Object.entries(loaders).filter(([path]) => path.startsWith(prefix)).map(([, load]) => load()));
  return Object.assign({}, ...parts.map((m) => m.default));
}

interface State {
  pref: LocalePref;
  locale: Locale;
  messages: Messages;
}
let state: State = { pref: 'auto', locale: DEFAULT_LOCALE, messages: {} };
const listeners = new Set<() => void>();

function apply(next: State) {
  state = next;
  document.documentElement.lang = next.locale;
  for (const l of listeners) l();
}

/** Carga el idioma inicial antes de pintar. Si el catálogo no llega (red caída), la interfaz queda en español. */
export async function initI18n(): Promise<void> {
  const pref = readPref();
  const locale = resolve(pref);
  try {
    apply({ pref, locale, messages: await loadMessages(locale) });
  } catch {
    apply({ pref, locale: DEFAULT_LOCALE, messages: {} });
  }
}

/** Cambia y recuerda la preferencia de este navegador ("auto" vuelve a seguir al navegador). */
export async function setLocalePref(pref: LocalePref): Promise<void> {
  try {
    if (pref === 'auto') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, pref);
  } catch {
    /* almacenamiento no disponible: vale solo para esta visita */
  }
  const locale = resolve(pref);
  apply({ pref, locale, messages: locale === state.locale ? state.messages : await loadMessages(locale) });
}

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/** Idioma activo y preferencia; el componente se vuelve a pintar al cambiar de idioma. */
export function useLocale(): { locale: Locale; pref: LocalePref } {
  const s = useSyncExternalStore(subscribe, () => state);
  return { locale: s.locale, pref: s.pref };
}

export const currentLocale = (): Locale => state.locale;

/**
 * Una misma palabra española con dos sentidos lleva contexto tras `@@` ("hermanos@@primos" = primos hermanos):
 * el contexto separa las traducciones y no se muestra en español.
 */
const source = (key: string) => {
  const i = key.indexOf('@@');
  return i === -1 ? key : key.slice(0, i);
};

const fill = (s: string, vars?: Vars) => (vars ? s.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m)) : s);

/** Texto traducido; `{nombre}` se reemplaza con `vars.nombre`. */
export function t(key: string, vars?: Vars): string {
  return fill(state.messages[key] ?? source(key), vars);
}

/** Como `t()`, pero los valores pueden ser elementos (un nombre resaltado dentro de una frase). */
export function tn(key: string, vars: Record<string, ReactNode>): ReactNode {
  const parts = (state.messages[key] ?? source(key)).split(/(\{\w+\})/);
  return createElement(
    Fragment,
    null,
    ...parts.map((p, i) => {
      const m = /^\{(\w+)\}$/.exec(p);
      return createElement(Fragment, { key: i }, m && m[1]! in vars ? vars[m[1]!] : p);
    }),
  );
}
